import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronRight, Loader2, RefreshCw, X } from 'lucide-react';
import {
  getNetworkExecution,
  listNetworkExecutions,
} from '@/services/networkExecutionsApi';
import { formatRelativeTime } from '@/utils/formatDate';
import { aslToReactFlow, isASL } from '@/utils/aslToReactFlow';
import GraphVisualization from './GraphVisualization';

/**
 * Workbench "Executions" lists **Brain** (stepflow-brain) runs only. The same
 * `GET /networks/{id}/executions` response also includes **agentic pool** runs
 * (and other pool-scoped tasks) which are stamped with a non-empty `poolId`
 * and `kind` like `pool_*` in DynamoDB. Those must not appear here — see
 * `get_network_executions_handler` and `_agentic_record_room_run_artifacts`
 * in the REST API.
 */
function isBrainBuildExecutionRow(row) {
  if (!row || typeof row !== 'object') return false;
  const poolId = row.poolId;
  if (poolId != null && String(poolId).trim() !== '') {
    return false;
  }
  const kind = String(row.kind || '').toLowerCase();
  if (kind.startsWith('pool_')) {
    return false;
  }
  return true;
}

const STATUS_META = Object.freeze({
  running: {
    label: 'Running',
    className: 'bg-blue-50 text-blue-700 ring-blue-200',
  },
  awaiting: {
    label: 'Awaiting approval',
    className: 'bg-amber-50 text-amber-800 ring-amber-200',
  },
  success: {
    label: 'Succeeded',
    className: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  },
  failed: {
    label: 'Failed',
    className: 'bg-rose-50 text-rose-700 ring-rose-200',
  },
  aborted: {
    label: 'Aborted',
    className: 'bg-slate-100 text-slate-700 ring-slate-200',
  },
  unknown: {
    label: 'Unknown',
    className: 'bg-slate-100 text-slate-700 ring-slate-200',
  },
});

function bucketStatus(raw) {
  const value = String(raw || '').trim().toUpperCase();
  if (!value) return 'unknown';
  if (value === 'AWAITING_APPROVAL' || value === 'PENDING_APPROVAL') return 'awaiting';
  if (value === 'RUNNING' || value === 'STARTED' || value === 'INITIATED') return 'running';
  if (value === 'SUCCESS' || value === 'SUCCEEDED' || value === 'COMPLETED') return 'success';
  if (value === 'FAILED' || value === 'ERROR' || value === 'TIMED_OUT') return 'failed';
  if (value === 'ABORTED' || value === 'CANCELED' || value === 'CANCELLED') return 'aborted';
  return 'unknown';
}

function StatusPill({ raw }) {
  const bucket = bucketStatus(raw);
  const meta = STATUS_META[bucket] || STATUS_META.unknown;
  const live = bucket === 'running' || bucket === 'awaiting';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset ${meta.className}`}
    >
      {live ? <Loader2 className="h-2.5 w-2.5 animate-spin" aria-hidden /> : null}
      {meta.label}
    </span>
  );
}

function PromptPreview({ value }) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) return <span className="text-slate-400">(no prompt)</span>;
  if (text.length <= 150) return <span className="text-slate-700">{text}</span>;
  return (
    <span className="text-slate-700" title={text}>
      {`${text.slice(0, 149)}...`}
    </span>
  );
}

/**
 * "Task done" cell — surfaces the smart-session classifier's read of
 * what each run accomplished. ``label`` is the short task name
 * ("update pytorch libs"); ``summary`` is the one-sentence description
 * stored alongside it on the Tasks row by ``stream_runner._stamp_task_row``.
 *
 * Both fields are best-effort — legacy rows (pre-classifier) and runs
 * that crashed before the stamp ran will have neither. Render a soft
 * em-dash placeholder so the column never looks broken; the rest of
 * the row still tells the user what happened (status, prompt, time).
 */
export function TaskDoneCell({ label, summary }) {
  const lbl = String(label || '').trim();
  const sum = String(summary || '').trim();
  if (!lbl && !sum) {
    return <span className="text-slate-300">—</span>;
  }
  // ``min-w-0`` lets ``truncate`` actually clip inside the table cell;
  // without it, flex children default to ``min-width: auto`` (content
  // size) and the cell grows past its column. Summary wraps to two
  // lines so the agent's outcome is readable without hovering.
  const tooltip = [lbl, sum].filter(Boolean).join('\n');
  return (
    <div className="flex min-w-0 flex-col gap-0.5" title={tooltip}>
      {lbl ? (
        <span className="truncate font-medium text-slate-700">{lbl}</span>
      ) : null}
      {sum ? (
        <span className="line-clamp-2 text-[11px] leading-snug text-slate-500">
          {sum}
        </span>
      ) : null}
    </div>
  );
}

function JsonBlock({ value }) {
  return (
    <pre className="max-h-80 overflow-auto rounded-lg border border-slate-200 bg-slate-950 px-3 py-2 text-xs leading-5 text-slate-100">
      {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
    </pre>
  );
}

function useExecutionGraph(detail) {
  return useMemo(() => {
    if (detail?.graphData?.nodes || detail?.graphData?.edges) {
      return detail.graphData;
    }
    const aslCandidate = detail?.asl?.asl ?? detail?.asl;
    if (!aslCandidate || !isASL(aslCandidate)) return null;
    const graph = aslToReactFlow(aslCandidate);
    return graph?.nodes?.length ? graph : null;
  }, [detail]);
}

function ExecutionDetailDrawer({ open, networkId, taskId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inflight = useRef(0);
  const graphData = useExecutionGraph(detail);
  const explanation = detail?.architectExplanation ?? detail?.design;
  const asl = detail?.asl?.asl ?? detail?.asl;

  useEffect(() => {
    if (!open || !networkId || !taskId) return undefined;
    const seq = inflight.current + 1;
    inflight.current = seq;
    const abort = new AbortController();
    setLoading(true);
    setError(null);
    setDetail(null);

    (async () => {
      try {
        const next = await getNetworkExecution(networkId, taskId, { signal: abort.signal });
        if (inflight.current !== seq) return;
        if (!next) {
          setError('Execution details are not available yet.');
          return;
        }
        setDetail(next);
      } catch (e) {
        if (inflight.current !== seq || e?.name === 'AbortError') return;
        setError(e?.message || 'Failed to load execution details');
      } finally {
        if (inflight.current === seq) setLoading(false);
      }
    })();

    return () => abort.abort();
  }, [open, networkId, taskId]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <>
          <motion.div
            key="workbench-exec-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-slate-950/30"
          />
          <motion.aside
            key="workbench-exec-drawer"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 260, damping: 30 }}
            className="fixed right-0 top-0 z-50 flex h-full w-full max-w-3xl flex-col bg-white shadow-xl"
          >
            <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="truncate text-sm font-semibold text-slate-900">
                    Execution detail
                  </h2>
                  <StatusPill raw={detail?.status} />
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
                  {detail?.createdAt ? (
                    <span>Created {formatRelativeTime(detail.createdAt)}</span>
                  ) : null}
                  {taskId ? (
                    <span className="max-w-[300px] truncate font-mono text-[10px]" title={taskId}>
                      {taskId}
                    </span>
                  ) : null}
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                aria-label="Close execution detail"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
              {loading ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  Loading execution details...
                </div>
              ) : null}

              {error ? (
                <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {error}
                </div>
              ) : null}

              {detail ? (
                <div className="space-y-5">
                  {detail.prompt ? (
                    <section>
                      <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                        Prompt
                      </h3>
                      <div className="whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm leading-6 text-slate-800">
                        {detail.prompt}
                      </div>
                    </section>
                  ) : null}

                  {explanation ? (
                    <section>
                      <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                        Architect explanation
                      </h3>
                      <div className="whitespace-pre-wrap rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm leading-6 text-slate-800">
                        {typeof explanation === 'string'
                          ? explanation
                          : JSON.stringify(explanation, null, 2)}
                      </div>
                    </section>
                  ) : null}

                  {graphData ? (
                    <section>
                      <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                        Flow diagram
                      </h3>
                      <div className="h-80 overflow-hidden rounded-lg border border-slate-200 bg-white">
                        <GraphVisualization data={graphData} />
                      </div>
                    </section>
                  ) : null}

                  {asl ? (
                    <section>
                      <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                        ASL JSON
                      </h3>
                      <JsonBlock value={asl} />
                    </section>
                  ) : null}

                  {detail.status === 'FAILED' && detail.error ? (
                    <section>
                      <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-rose-600">
                        Error
                      </h3>
                      <pre className="max-h-72 overflow-auto rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs leading-5 text-rose-800">
                        {String(detail.error)}
                      </pre>
                    </section>
                  ) : null}

                  {detail.lastInput ? (
                    <section>
                      <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                        Last input
                      </h3>
                      <JsonBlock value={detail.lastInput} />
                    </section>
                  ) : null}
                </div>
              ) : null}
            </div>
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}

export default function WorkbenchExecutionsTab({ networkId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTaskId, setSelectedTaskId] = useState(null);

  const refresh = useCallback(async ({ signal } = {}) => {
    if (!networkId || networkId === 'new') {
      setRows([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { executions } = await listNetworkExecutions(networkId, { signal });
      const all = Array.isArray(executions) ? executions : [];
      setRows(all.filter(isBrainBuildExecutionRow));
    } catch (e) {
      if (e?.name === 'AbortError') return;
      setError(e?.message || 'Failed to load executions');
    } finally {
      setLoading(false);
    }
  }, [networkId]);

  useEffect(() => {
    const abort = new AbortController();
    refresh({ signal: abort.signal });
    return () => abort.abort();
  }, [refresh]);

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const left = Date.parse(a.createdAt || '') || 0;
      const right = Date.parse(b.createdAt || '') || 0;
      return right - left;
    });
  }, [rows]);

  if (!networkId || networkId === 'new') {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center bg-white px-6 text-center">
        <div>
          <p className="text-sm font-medium text-slate-800">No executions yet</p>
          <p className="mt-1 max-w-md text-xs text-slate-500">
            Create the network first, then Brain executions will appear here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-white">
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-slate-50/60 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">Executions</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Architect / Brain (stepflow-brain) runs only. Agentic pool runs stay in
            the Agentic Network room.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refresh()}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" aria-hidden />
          )}
          Refresh
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {error ? (
          <div className="m-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </div>
        ) : null}

        {!loading && !error && sortedRows.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center px-6 py-10 text-center">
            <p className="text-sm font-medium text-slate-800">No previous executions</p>
            <p className="mt-1 max-w-md text-xs text-slate-500">
              Test or deploy this network from Build and the run history will appear here.
            </p>
          </div>
        ) : null}

        {/*
         * Layout note: ``table-fixed`` + a single flex column made
         * ``Prompt`` absorb all leftover width and squeezed ``Task
         * done`` to a stub. Auto layout (default) splits the flex
         * budget between Prompt and Task done while the small
         * columns hold their min widths. ``Task done`` typically
         * carries the more useful information once a run lands —
         * give it the larger share.
         */}
        {sortedRows.length > 0 ? (
          <table className="w-full text-left text-xs">
            <colgroup>
              <col style={{ width: '120px' }} />
              <col style={{ width: '36%' }} />
              <col style={{ width: '50%' }} />
              <col style={{ width: '140px' }} />
              <col style={{ width: '40px' }} />
            </colgroup>
            <thead className="sticky top-0 z-10 bg-white/95 text-[10px] font-semibold uppercase tracking-wide text-slate-500 shadow-[0_1px_0_0_rgba(15,23,42,0.06)]">
              <tr>
                <th className="px-4 py-2">Status</th>
                <th className="py-2">Prompt</th>
                <th className="py-2">Task done</th>
                <th className="px-3 py-2 text-right">Created</th>
                <th aria-hidden />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sortedRows.map((row) => (
                <tr
                  key={row.taskId}
                  onClick={() => setSelectedTaskId(row.taskId)}
                  className={`group cursor-pointer transition-colors ${
                    selectedTaskId === row.taskId ? 'bg-blue-50/60' : 'hover:bg-slate-50'
                  }`}
                >
                  <td className="px-4 py-2">
                    <StatusPill raw={row.status} />
                  </td>
                  <td className="py-2 pr-3">
                    <PromptPreview value={row.prompt} />
                  </td>
                  <td className="py-2 pr-3 align-top">
                    <TaskDoneCell label={row.taskLabel} summary={row.taskSummary} />
                  </td>
                  <td className="px-3 py-2 text-right text-slate-500">
                    {formatRelativeTime(row.createdAt)}
                  </td>
                  <td className="pr-4 text-slate-300 group-hover:text-slate-500">
                    <ChevronRight className="h-4 w-4" aria-hidden />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>

      <ExecutionDetailDrawer
        open={!!selectedTaskId}
        networkId={networkId}
        taskId={selectedTaskId}
        onClose={() => setSelectedTaskId(null)}
      />
    </div>
  );
}
